import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { RotateCcw, Save, FolderOpen, ChevronDown, Trash2, Plus, X, Search, Loader2, Sparkles, FileText, Globe } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useCascadeStore } from './store'
import { CascadeCanvas } from './components/CascadeCanvas'
import { InfoPanel } from './components/InfoPanel'
import { COUNTRIES } from '@features/dashboards/components/PocTool/data/countries'
import { dealsApi, cascadeApi, scopesApi } from '@api/client'
import { showToast } from '@store/uiStore'
import { useTranscripts } from '@features/scope/components/TranscriptsModal'
import type { Deal } from '@api/types'
import type { CascadeNode, CascadeEdge } from './types'

// ─── Deal Search ────────────────────────────────────────────────────────

function DealSearch() {
  const { dealId, dealName, setDeal } = useCascadeStore()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Deal[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return }
    setIsSearching(true)
    try {
      const res = await dealsApi.list({ search: q, pageSize: 8 })
      if (res.success && res.data) {
        setResults(res.data.items)
      }
    } finally {
      setIsSearching(false)
    }
  }, [])

  useEffect(() => {
    if (query.length < 2) { setResults([]); return }
    const timer = setTimeout(() => search(query), 300)
    return () => clearTimeout(timer)
  }, [query, search])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const selectDeal = (deal: Deal) => {
    setDeal(deal.id, deal.customer_name)
    setQuery('')
    setIsOpen(false)
    setResults([])
  }

  const clearDeal = () => {
    setDeal(null, '')
    setQuery('')
  }

  if (dealId && dealName) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30">
        <span className="text-xs font-semibold text-blue-400">{dealName}</span>
        <button onClick={clearDeal} className="text-blue-400/60 hover:text-blue-400 transition-colors">
          <X size={12} />
        </button>
      </div>
    )
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
          {isSearching ? (
            <Loader2 size={12} className="text-[var(--text-muted)] animate-spin" />
          ) : (
            <Search size={12} className="text-[var(--text-muted)]" />
          )}
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(e.target.value.length >= 2) }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          placeholder="Search deals..."
          className="w-44 text-xs pl-7 pr-2.5 py-1.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl z-50 overflow-hidden">
          {isSearching ? (
            <div className="p-4 text-center">
              <Loader2 size={16} className="text-blue-400 animate-spin mx-auto mb-1" />
              <p className="text-[10px] text-[var(--text-muted)]">Searching...</p>
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-60 overflow-y-auto">
              {results.map(deal => (
                <button
                  key={deal.id}
                  onClick={() => selectDeal(deal)}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                >
                  <div className="text-xs font-medium text-[var(--text-primary)] truncate">{deal.customer_name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {deal.hubspot_deal_name && (
                      <span className="text-[10px] text-[var(--text-muted)] truncate">{deal.hubspot_deal_name}</span>
                    )}
                    <span className={`text-[8px] px-1.5 py-0.5 rounded font-medium ml-auto shrink-0 ${
                      deal.status === 'finalized' ? 'bg-emerald-500/15 text-emerald-400'
                        : deal.status === 'in_review' ? 'bg-amber-500/15 text-amber-400'
                        : 'bg-gray-500/15 text-[var(--text-muted)]'
                    }`}>
                      {deal.status}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center">
              <p className="text-[10px] text-[var(--text-muted)]">No deals found for &quot;{query}&quot;</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ─── Country Search ────────────────────────────────────────────────────

function CountrySelector() {
  const { country, countryCode, setCountry } = useCascadeStore()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search) return COUNTRIES.slice(0, 30)
    const q = search.toLowerCase()
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    ).slice(0, 30)
  }, [search])

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-lg text-xs transition-colors ${
          countryCode
            ? 'px-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] hover:border-blue-400'
            : 'px-4 py-2.5 bg-blue-600 hover:bg-blue-500 border-2 border-blue-400 animate-pulse'
        }`}
      >
        {countryCode ? (
          <>
            <span className="text-base">{countryCodeToFlag(countryCode)}</span>
            <span className="text-[var(--text-primary)] font-medium">{country}</span>
          </>
        ) : (
          <span className="text-white font-semibold">Select a country to begin</span>
        )}
        <ChevronDown size={12} className={countryCode ? 'text-[var(--text-muted)]' : 'text-white/70'} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-64 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="p-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="w-full px-3 py-1.5 text-xs rounded-md bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filtered.map(c => (
                <button
                  key={c.code}
                  onClick={() => {
                    setCountry(c.name, c.code)
                    setOpen(false)
                    setSearch('')
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[var(--bg-tertiary)] transition-colors ${
                    c.code === countryCode ? 'bg-blue-500/10 text-blue-400' : 'text-[var(--text-primary)]'
                  }`}
                >
                  <span className="text-base">{countryCodeToFlag(c.code)}</span>
                  <span>{c.name}</span>
                  <span className="text-[var(--text-muted)] ml-auto">{c.code}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function countryCodeToFlag(code: string): string {
  try {
    return code
      .toUpperCase()
      .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
  } catch {
    return code
  }
}

// ─── Shared helper: fetch blueprint context from API ──────────────────

async function fetchBlueprintContext(dealId: number) {
  try {
    const scopeRes = await scopesApi.getByDealId(dealId)
    if (scopeRes.success && scopeRes.data) {
      const scope = scopeRes.data as unknown as Record<string, unknown>
      const generalInfo = typeof scope.general_info === 'string' ? JSON.parse(scope.general_info) : {}
      const bp = generalInfo._blueprint || {}
      return {
        context: {
          businessModel: bp.businessModel || undefined,
          monthlyOnboardingVolume: bp.monthlyOnboardingVolume || undefined,
          strategicPriorities: bp.strategicPriorities || undefined,
          stpRate: bp.stpRate || undefined,
          fraudRate: bp.fraudRate || undefined,
          integrationMethod: bp.integrationMethod || undefined,
          jurisdictionsInScope: bp.jurisdictionsInScope || undefined,
          onboardingJourney: bp.onboardingJourney || undefined,
          checksBeforeTransact: bp.checksBeforeTransact || undefined,
          verificationStack: (bp.verificationStack || [])
            .filter((v: { vendor: string }) => v.vendor)
            .map((v: { layer: string; vendor: string; painPoints: string }) => ({
              layer: v.layer,
              vendor: v.vendor,
              painPoints: v.painPoints,
            })),
        },
        // Extract country rulesets from blueprint for auto-detect
        countryRulesets: (bp.countryRulesets || []) as Array<{ country: string; ruleset: string }>,
      }
    }
  } catch {
    // Proceed without blueprint context
  }
  return { context: undefined, countryRulesets: [] }
}

// ─── Main Component ────────────────────────────────────────────────────

export function CascadeBuilder() {
  const {
    country, countryCode, countryCascades, dealId, nodes,
    addCountry, switchCountry, removeCountry, setDeal,
    saveProject, resetCascade, savedProjects, loadProject, deleteProject,
  } = useCascadeStore()

  const [searchParams] = useSearchParams()
  const [showSaved, setShowSaved] = useState(false)
  const [showAddCountry, setShowAddCountry] = useState(false)
  const [addCountrySearch, setAddCountrySearch] = useState('')
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiProgress, setAiProgress] = useState('')
  const [showAiModal, setShowAiModal] = useState(false)
  const [aiNotes, setAiNotes] = useState('')
  const [aiIncludeHubspot, setAiIncludeHubspot] = useState(true)
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<Set<number>>(new Set())
  const { transcripts: savedTranscripts, loading: transcriptsLoading } = useTranscripts(showAiModal && dealId ? dealId : null)

  // AI modal country picker
  const [aiCountries, setAiCountries] = useState<Array<{ country: string; countryCode: string }>>([])
  const [aiCountrySearch, setAiCountrySearch] = useState('')
  const [showAiCountryPicker, setShowAiCountryPicker] = useState(false)
  const [blueprintCountries, setBlueprintCountries] = useState<Array<{ country: string; countryCode: string }>>([])

  // Auto-select all transcripts when they load
  useEffect(() => {
    if (savedTranscripts.length > 0) {
      setSelectedTranscriptIds(new Set(savedTranscripts.map(t => t.id)))
    }
  }, [savedTranscripts])

  // Auto-set deal from URL params (when opened from Blueprint)
  useEffect(() => {
    const urlDealId = searchParams.get('dealId')
    const urlDealName = searchParams.get('dealName')
    if (urlDealId && urlDealName && !dealId) {
      setDeal(Number(urlDealId), urlDealName)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load cascade projects from DB when deal is set, and auto-open a specific project if projectId is in the URL
  useEffect(() => {
    if (dealId) {
      useCascadeStore.getState().loadProjectsFromDb(dealId).then(() => {
        const urlProjectId = searchParams.get('projectId')
        if (urlProjectId) {
          useCascadeStore.getState().loadProject(urlProjectId)
        }
      })
    }
  }, [dealId]) // eslint-disable-line react-hooks/exhaustive-deps

  // When AI modal opens, auto-detect countries from blueprint
  useEffect(() => {
    if (!showAiModal || !dealId) {
      setBlueprintCountries([])
      return
    }
    fetchBlueprintContext(dealId).then(({ countryRulesets }) => {
      const detected: Array<{ country: string; countryCode: string }> = []
      for (const cr of countryRulesets) {
        if (!cr.country) continue
        const match = COUNTRIES.find(c => c.code === cr.country)
        if (match) detected.push({ country: match.name, countryCode: match.code })
      }
      setBlueprintCountries(detected)
      // Pre-select detected countries if user hasn't manually picked any
      if (detected.length > 0 && aiCountries.length === 0) {
        setAiCountries(detected)
      }
    })
  }, [showAiModal, dealId]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTranscript = (id: number) => {
    setSelectedTranscriptIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAiCountry = (c: { country: string; countryCode: string }) => {
    setAiCountries(prev => {
      if (prev.some(p => p.countryCode === c.countryCode)) {
        return prev.filter(p => p.countryCode !== c.countryCode)
      }
      return [...prev, c]
    })
  }

  const aiCountryFiltered = useMemo(() => {
    if (!aiCountrySearch) return COUNTRIES.slice(0, 30)
    const q = aiCountrySearch.toLowerCase()
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    ).slice(0, 30)
  }, [aiCountrySearch])

  const handleAiGenerate = useCallback(async () => {
    if (aiCountries.length === 0) {
      showToast('Select at least one country to generate', 'warning')
      return
    }

    // Confirm if there's existing data
    const existingCountries = [
      ...(countryCode ? [countryCode] : []),
      ...countryCascades.map(c => c.countryCode),
    ]
    const willReplace = aiCountries.some(c => existingCountries.includes(c.countryCode))
    if (willReplace) {
      if (!confirm('This will replace existing cascades for overlapping countries. Continue?')) return
    }

    setAiGenerating(true)
    try {
      // Fetch blueprint context
      let blueprintContext: Parameters<typeof cascadeApi.aiGenerateMulti>[0]['blueprintContext'] = undefined
      if (dealId) {
        const { context } = await fetchBlueprintContext(dealId)
        blueprintContext = context
      }

      // Combine notes + transcripts
      const transcriptTexts = savedTranscripts
        .filter(t => selectedTranscriptIds.has(t.id))
        .map(t => `## Transcript: ${t.title} (${t.call_date})\n${t.transcript}`)
      const combinedNotes = [aiNotes.trim(), ...transcriptTexts].filter(Boolean).join('\n\n---\n\n')

      if (aiCountries.length === 1) {
        // Single country — use the existing endpoint (faster)
        const c = aiCountries[0]
        setAiProgress(`Generating cascade for ${c.country}...`)
        const res = await cascadeApi.aiGenerate({
          country: c.country,
          countryCode: c.countryCode,
          dealId,
          blueprintContext,
          userNotes: combinedNotes || undefined,
          includeHubspot: aiIncludeHubspot && !!dealId,
        })

        if (!res.success || !res.data) {
          showToast(res.error || 'Failed to generate cascade', 'error')
          return
        }

        // Load into store — set as active country
        const genNodes = res.data.nodes as CascadeNode[]
        const genEdges = res.data.edges as CascadeEdge[]

        // If this country already exists, replace it
        if (c.countryCode === countryCode) {
          useCascadeStore.setState({
            nodes: genNodes,
            edges: genEdges,
            notes: res.data.notes || '',
            selectedNodeId: null,
            activeChoice: null,
            fadingChoiceIds: [],
          })
        } else {
          // Remove from countryCascades if exists, then add as new active
          const filtered = countryCascades.filter(cc => cc.countryCode !== c.countryCode)
          // Save current active to cascades if it exists
          const currentCascades = countryCode ? [
            ...filtered,
            { country, countryCode, nodes, edges: useCascadeStore.getState().edges, notes: useCascadeStore.getState().notes },
          ] : filtered

          useCascadeStore.setState({
            country: c.country,
            countryCode: c.countryCode,
            nodes: genNodes,
            edges: genEdges,
            notes: res.data.notes || '',
            countryCascades: currentCascades,
            selectedNodeId: null,
            activeChoice: null,
            fadingChoiceIds: [],
          })
        }

        showToast(`Cascade generated for ${c.country}`, 'success')
      } else {
        // Multi-country — use the batch endpoint
        setAiProgress(`Generating cascades for ${aiCountries.length} countries...`)
        const res = await cascadeApi.aiGenerateMulti({
          countries: aiCountries,
          dealId,
          blueprintContext,
          userNotes: combinedNotes || undefined,
          includeHubspot: aiIncludeHubspot && !!dealId,
        })

        if (!res.success || !res.data) {
          showToast(res.error || 'Failed to generate cascades', 'error')
          return
        }

        const { cascades, errors } = res.data

        if (cascades.length === 0) {
          showToast('No cascades were generated. ' + (errors.length > 0 ? errors[0].error : ''), 'error')
          return
        }

        // Deduplicate cascades by country code — keep the one with the most nodes
        const dedupedCascades = cascades.reduce((acc, c) => {
          const existing = acc.find(x => x.countryCode === c.countryCode)
          if (!existing) {
            acc.push(c)
          } else if ((c.nodes as unknown[]).length > (existing.nodes as unknown[]).length) {
            acc[acc.indexOf(existing)] = c
          }
          return acc
        }, [] as typeof cascades)

        // Load first cascade as active, rest into countryCascades
        const first = dedupedCascades[0]
        const rest = dedupedCascades.slice(1)

        // Preserve existing cascades that weren't regenerated
        const generatedCodes = new Set(cascades.map(c => c.countryCode))
        const preservedCascades = countryCascades.filter(c => !generatedCodes.has(c.countryCode))

        // Save current active if not being replaced
        if (countryCode && !generatedCodes.has(countryCode)) {
          preservedCascades.push({
            country,
            countryCode,
            nodes,
            edges: useCascadeStore.getState().edges,
            notes: useCascadeStore.getState().notes,
          })
        }

        useCascadeStore.setState({
          country: first.country,
          countryCode: first.countryCode,
          nodes: first.nodes as CascadeNode[],
          edges: first.edges as CascadeEdge[],
          notes: first.notes || '',
          countryCascades: [
            ...preservedCascades,
            ...rest.map(c => ({
              country: c.country,
              countryCode: c.countryCode,
              nodes: c.nodes as CascadeNode[],
              edges: c.edges as CascadeEdge[],
              notes: c.notes || '',
            })),
          ],
          selectedNodeId: null,
          activeChoice: null,
          fadingChoiceIds: [],
        })

        const msg = `Generated ${cascades.length} cascade${cascades.length > 1 ? 's' : ''}`
        if (errors.length > 0) {
          showToast(`${msg} (${errors.length} failed)`, 'warning')
        } else {
          showToast(msg, 'success')
        }
      }

      setShowAiModal(false)
      setAiCountries([])
    } catch (error) {
      console.error('AI generate error:', error)
      showToast('Failed to generate cascade', 'error')
    } finally {
      setAiGenerating(false)
      setAiProgress('')
    }
  }, [aiCountries, countryCode, country, countryCascades, dealId, nodes, aiNotes, aiIncludeHubspot, savedTranscripts, selectedTranscriptIds])

  const addCountryFiltered = useMemo(() => {
    if (!addCountrySearch) return COUNTRIES.slice(0, 30)
    const q = addCountrySearch.toLowerCase()
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    ).slice(0, 30)
  }, [addCountrySearch])

  // All countries in this project (active + stored)
  const allCountries = useMemo(() => {
    const list: { country: string; countryCode: string; active: boolean }[] = []
    if (countryCode) {
      list.push({ country, countryCode, active: true })
    }
    for (const c of countryCascades) {
      list.push({ country: c.country, countryCode: c.countryCode, active: false })
    }
    return list
  }, [country, countryCode, countryCascades])

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]">
      {/* Deal-level header (above the cascade window) */}
      <div className="flex items-center gap-4 mb-3">
        <DealSearch />

        {/* AI Generate button — top level, outside country selection */}
        <button
          onClick={() => setShowAiModal(true)}
          disabled={aiGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-60 disabled:cursor-wait transition-colors"
        >
          {aiGenerating ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Sparkles size={12} />
          )}
          {aiGenerating ? (aiProgress || 'Generating...') : 'AI Generate'}
        </button>

        <div className="flex-1" />

        {/* Save / Load / Reset actions */}
        <div className="flex items-center gap-1.5">
          {savedProjects.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowSaved(!showSaved)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <FolderOpen size={12} /> Saved ({savedProjects.length})
              </button>
              {showSaved && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowSaved(false)} />
                  <div className="absolute right-0 top-full mt-1 w-72 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl z-50 overflow-hidden">
                    {savedProjects.map(p => (
                      <div key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-tertiary)] group">
                        <button
                          onClick={() => { loadProject(p.id); setShowSaved(false) }}
                          className="flex-1 text-left"
                        >
                          <div className="text-xs font-medium text-[var(--text-primary)]">
                            {p.dealName || 'Untitled'}
                          </div>
                          <div className="text-[10px] text-[var(--text-muted)]">
                            {p.cascades.map(c => `${countryCodeToFlag(c.countryCode)} ${c.country}`).join(' · ')}
                          </div>
                        </button>
                        <button
                          onClick={() => deleteProject(p.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-300 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <button
            onClick={saveProject}
            disabled={!country}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={12} /> Save
          </button>
          <button
            onClick={resetCascade}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-red-400 transition-colors"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      {/* Cascade window */}
      <div className="flex-1 flex flex-col bg-[var(--bg-primary)] rounded-xl border border-[var(--border-color)] overflow-hidden">
        {/* Country toolbar */}
        <div className="shrink-0 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <div className="flex items-center gap-3 px-4 py-2">
            {/* Country tabs */}
            {allCountries.map(c => (
              <div key={c.countryCode} className="flex items-center group">
                <button
                  onClick={() => !c.active && switchCountry(c.countryCode)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-l-lg transition-colors ${
                    c.active
                      ? 'bg-blue-500/10 text-blue-400 font-semibold border border-blue-500/30 border-r-0'
                      : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-transparent border-r-0'
                  }`}
                >
                  <span className="text-sm">{countryCodeToFlag(c.countryCode)}</span>
                  {c.country}
                </button>
                {allCountries.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeCountry(c.countryCode) }}
                    className={`px-1 py-1.5 text-[10px] rounded-r-lg transition-colors opacity-0 group-hover:opacity-100 ${
                      c.active
                        ? 'bg-blue-500/10 text-blue-400/60 hover:text-red-400 border border-blue-500/30 border-l-0'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-red-400 border border-transparent border-l-0'
                    }`}
                    title={`Remove ${c.country}`}
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            ))}

            {/* Add country button / initial country selector */}
            {allCountries.length === 0 ? (
              <CountrySelector />
            ) : (
              <div className="relative">
                <button
                  onClick={() => setShowAddCountry(!showAddCountry)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] rounded-lg text-[var(--text-muted)] hover:text-blue-400 border border-dashed border-[var(--border-color)] hover:border-blue-400 transition-colors"
                >
                  <Plus size={12} /> Add Country
                </button>
                {showAddCountry && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => { setShowAddCountry(false); setAddCountrySearch('') }} />
                    <div className="absolute top-full left-0 mt-1 w-56 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl z-50 overflow-hidden">
                      <div className="p-2">
                        <input
                          type="text"
                          value={addCountrySearch}
                          onChange={(e) => setAddCountrySearch(e.target.value)}
                          placeholder="Search countries..."
                          className="w-full px-3 py-1.5 text-xs rounded-md bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {addCountryFiltered.map(c => {
                          const alreadyAdded = allCountries.some(ac => ac.countryCode === c.code)
                          return (
                            <button
                              key={c.code}
                              disabled={alreadyAdded}
                              onClick={() => {
                                addCountry(c.name, c.code)
                                setShowAddCountry(false)
                                setAddCountrySearch('')
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                                alreadyAdded
                                  ? 'opacity-40 cursor-not-allowed'
                                  : 'hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)]'
                              }`}
                            >
                              <span className="text-sm">{countryCodeToFlag(c.code)}</span>
                              <span>{c.name}</span>
                              {alreadyAdded && <span className="ml-auto text-[9px] text-[var(--text-muted)]">added</span>}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* AI Generate Modal */}
        {showAiModal && (
          <>
            <div className="fixed inset-0 bg-black/60 z-40" onClick={() => !aiGenerating && setShowAiModal(false)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-2xl bg-[var(--bg-secondary)] rounded-lg shadow-modal" role="dialog" aria-modal="true">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/20 bg-violet-500/5 rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-400" />
                    <span className="text-sm font-semibold text-violet-400">AI Cascade Generator</span>
                    {aiCountries.length > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold bg-blue-500/20 text-blue-300 rounded">
                        {aiCountries.length} {aiCountries.length === 1 ? 'country' : 'countries'}
                      </span>
                    )}
                  </div>
                  <button onClick={() => !aiGenerating && setShowAiModal(false)} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  <p className="text-xs text-[var(--text-secondary)]">
                    Select countries to generate cascades for, add context from transcripts or notes, and the AI will design verification flows for each country.
                  </p>

                  {/* Country selector */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                      <Globe className="w-3 h-3" /> Countries
                    </p>

                    {/* Selected country pills */}
                    <div className="flex flex-wrap gap-1.5">
                      {aiCountries.map(c => (
                        <span
                          key={c.countryCode}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30"
                        >
                          {countryCodeToFlag(c.countryCode)} {c.country}
                          <button
                            onClick={() => toggleAiCountry(c)}
                            className="hover:text-blue-200 transition-colors"
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      <div className="relative">
                        <button
                          onClick={() => setShowAiCountryPicker(!showAiCountryPicker)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full border border-dashed border-[var(--border-color)] text-[var(--text-muted)] hover:text-blue-400 hover:border-blue-400 transition-colors"
                        >
                          <Plus size={10} /> Add country
                        </button>
                        {showAiCountryPicker && (
                          <>
                            <div className="fixed inset-0 z-50" onClick={() => { setShowAiCountryPicker(false); setAiCountrySearch('') }} />
                            <div className="absolute top-full left-0 mt-1 w-56 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl z-[60] overflow-hidden">
                              <div className="p-2">
                                <input
                                  type="text"
                                  value={aiCountrySearch}
                                  onChange={(e) => setAiCountrySearch(e.target.value)}
                                  placeholder="Search countries..."
                                  className="w-full px-3 py-1.5 text-xs rounded-md bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  autoFocus
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {aiCountryFiltered.map(c => {
                                  const selected = aiCountries.some(ac => ac.countryCode === c.code)
                                  return (
                                    <button
                                      key={c.code}
                                      onClick={() => {
                                        toggleAiCountry({ country: c.name, countryCode: c.code })
                                        if (!selected) {
                                          setShowAiCountryPicker(false)
                                          setAiCountrySearch('')
                                        }
                                      }}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                                        selected
                                          ? 'bg-blue-500/10 text-blue-400'
                                          : 'hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)]'
                                      }`}
                                    >
                                      <span className="text-sm">{countryCodeToFlag(c.code)}</span>
                                      <span>{c.name}</span>
                                      {selected && <span className="ml-auto text-[9px] text-blue-400">selected</span>}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Blueprint auto-detect hint */}
                    {blueprintCountries.length > 0 && aiCountries.length === 0 && (
                      <button
                        onClick={() => setAiCountries(blueprintCountries)}
                        className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        Use {blueprintCountries.length} countries from Blueprint ({blueprintCountries.map(c => c.country).join(', ')})
                      </button>
                    )}
                  </div>

                  {/* Saved transcripts (only when deal is linked) */}
                  {dealId && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                        <FileText className="w-3 h-3" /> Saved Transcripts
                        {savedTranscripts.length > 0 && (
                          <span className="text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] px-1.5 py-0.5 rounded">{savedTranscripts.length}</span>
                        )}
                      </p>

                      {transcriptsLoading && (
                        <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading transcripts...</p>
                      )}

                      {savedTranscripts.length > 0 ? (
                        <div className="grid gap-1.5">
                          {savedTranscripts.map(t => {
                            const selected = selectedTranscriptIds.has(t.id)
                            const wordCount = t.transcript.split(/\s+/).filter(Boolean).length
                            return (
                              <label
                                key={t.id}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                  selected
                                    ? 'border-violet-500/40 bg-violet-500/10'
                                    : 'border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--bg-tertiary)]'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleTranscript(t.id)}
                                  className="rounded border-[var(--border-color)]"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-medium text-[var(--text-primary)]">{t.title}</span>
                                  <span className="text-[10px] text-[var(--text-muted)] ml-2">{t.call_date}</span>
                                </div>
                                <span className="text-[10px] text-[var(--text-muted)] shrink-0">{wordCount.toLocaleString()} words</span>
                              </label>
                            )
                          })}
                        </div>
                      ) : !transcriptsLoading ? (
                        <p className="text-[11px] text-[var(--text-muted)] italic py-1">No transcripts saved for this deal.</p>
                      ) : null}
                    </div>
                  )}

                  {/* Notes / raw instructions */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Instructions / Notes</p>
                    <textarea
                      value={aiNotes}
                      onChange={(e) => setAiNotes(e.target.value)}
                      rows={4}
                      placeholder="e.g. 'Use Onfido for document verification and ComplyAdvantage for AML. Include a manual review step for high-risk customers. They need 1+1 ruleset...'"
                      className="w-full px-3 py-2 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-violet-500 resize-y"
                    />
                  </div>

                  {/* HubSpot toggle (only when deal is linked) */}
                  {dealId && (
                    <label className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors">
                      <input
                        type="checkbox"
                        checked={aiIncludeHubspot}
                        onChange={(e) => setAiIncludeHubspot(e.target.checked)}
                        className="rounded border-[var(--border-color)]"
                      />
                      <div className="flex-1">
                        <span className="text-xs font-medium text-[var(--text-primary)]">Include HubSpot data</span>
                        <p className="text-[10px] text-[var(--text-muted)]">Pull deal properties, notes, and activity from HubSpot for richer context</p>
                      </div>
                    </label>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-color)] bg-[var(--bg-primary)] rounded-b-lg">
                  <p className="text-[10px] text-[var(--text-muted)]">
                    {aiCountries.length > 1
                      ? `Will generate ${aiCountries.length} cascades sequentially.`
                      : 'Blueprint context is included automatically when a deal is linked.'}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowAiModal(false)}
                      disabled={aiGenerating}
                      className="px-3 py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAiGenerate}
                      disabled={aiGenerating || aiCountries.length === 0}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-60 disabled:cursor-wait transition-colors"
                    >
                      {aiGenerating ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Sparkles size={12} />
                      )}
                      {aiGenerating
                        ? (aiProgress || 'Generating...')
                        : aiCountries.length > 1
                          ? `Generate ${aiCountries.length} Cascades`
                          : 'Generate Cascade'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main content area */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Canvas */}
          <div className="flex-1 relative">
            <CascadeCanvas />
          </div>

          {/* Info panel (right sidebar) */}
          <InfoPanel />
        </div>
      </div>
    </div>
  )
}

// Export the helper for use from BlueprintTab
export { fetchBlueprintContext, countryCodeToFlag }
